import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Users, UserCheck, Clock, Award, Mail, TrendingUp, Download, BarChart3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgressBar } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND = BACKEND_URL;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-surface p-5" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className={accent ? "h-5 w-5 text-[#33B6E4]" : "h-5 w-5 text-muted-foreground"} />
      </div>
      <div className="tnum mt-2 font-heading text-[28px] font-semibold">{value}</div>
    </div>
  );
}

function DropOffModal({ courseId, onClose }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    if (courseId) {
      setA(null);
      api.get(`/admin/courses/${courseId}/analytics`).then(({ data }) => setA(data));
    }
  }, [courseId]);

  return (
    <Dialog open={!!courseId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dropoff-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{a ? a.course.title : "Loading…"}</DialogTitle>
          <DialogDescription>Lesson-by-lesson completion and assessment funnel for this course.</DialogDescription>
        </DialogHeader>
        {!a ? (
          <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 rounded" />)}</div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-6 text-sm">
              <div><span className="text-muted-foreground">Enrolled: </span><span className="tnum font-semibold">{a.enrolled}</span></div>
              {a.assessment && (
                <>
                  <div><span className="text-muted-foreground">Assessment attempts: </span><span className="tnum font-semibold">{a.assessment.attempted}</span></div>
                  <div><span className="text-muted-foreground">Passed: </span><span className="tnum font-semibold">{a.assessment.passed}</span> <span className="text-muted-foreground">({a.assessment.pass_rate}%)</span></div>
                </>
              )}
            </div>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lesson-by-lesson completion (drop-off)</p>
              <div className="space-y-2.5" data-testid="dropoff-lessons">
                {a.lessons.map((l, i) => {
                  const prev = i > 0 ? a.lessons[i - 1].completed : l.completed;
                  const dropped = prev - l.completed;
                  return (
                    <div key={l.id} className="grid grid-cols-[24px_1fr_120px] items-center gap-3" data-testid={`dropoff-lesson-${l.id}`}>
                      <span className="tnum text-xs text-muted-foreground">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{l.title}</div>
                        <ProgressBar value={l.rate} className="mt-1" />
                      </div>
                      <div className="text-right text-sm">
                        <span className="tnum font-semibold">{l.completed}</span>
                        <span className="text-muted-foreground"> · {l.rate}%</span>
                        {i > 0 && dropped > 0 && (
                          <span className="ml-1 text-xs text-red-500" title="Learners lost vs previous lesson">↓{dropped}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {a.lessons.length === 0 && <p className="text-sm text-muted-foreground">No lessons in this course yet.</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [drillCourse, setDrillCourse] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);

  if (!d) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0,1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-[20px] font-semibold">Overview</h2>
        <Button variant="outline" onClick={() => window.open(`${BACKEND}/api/admin/cpd/export.csv`, "_blank")} data-testid="org-cpd-export">
          <Download className="mr-2 h-4 w-4" /> Export org CPD (CSV)
        </Button>
      </div>

      {!d.email_configured && (
        <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-sm text-muted-foreground" data-testid="email-not-configured-notice">
          <Mail className="mr-2 inline h-4 w-4" />
          Email delivery is not configured. Invitations run in <strong>copy-link</strong> mode. Add a Resend key to enable real sends.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Total users" value={d.total_users} />
        <Stat icon={UserCheck} label="Activated" value={d.activated} accent />
        <Stat icon={Mail} label="Invited" value={d.invited} />
        <Stat icon={Clock} label="Not invited" value={d.not_invited} />
        <Stat icon={Users} label="Partners" value={d.partners} />
        <Stat icon={Users} label="Internal staff" value={d.internal_staff} />
        <Stat icon={Award} label="Total CPD hours" value={d.total_cpd_hours} accent />
        <Stat icon={TrendingUp} label="Courses" value={d.courses.length} />
      </div>

      <div>
        <h2 className="mb-4 font-heading text-[20px] font-semibold">Per-course analytics</h2>
        <div className="card-surface overflow-hidden">
          <div className="hidden grid-cols-[1fr_120px_120px_1fr] gap-4 border-b border-border bg-secondary/50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
            <div>Course</div><div>Enrolled</div><div>Completed</div><div>Completion rate</div>
          </div>
          <div className="divide-y divide-border">
            {d.courses.map((c) => (
              <button key={c.id} onClick={() => setDrillCourse(c.id)} className="grid w-full grid-cols-2 gap-3 px-5 py-4 text-left transition-colors hover:bg-accent/50 sm:grid-cols-[1fr_120px_120px_1fr] sm:items-center" data-testid={`course-analytics-${c.id}`}>
                <div className="col-span-2 flex items-center gap-2 font-medium sm:col-span-1">
                  <BarChart3 className="h-4 w-4 shrink-0 text-[#33B6E4]" />
                  <span className="truncate">{c.title}</span>
                </div>
                <div className="tnum text-sm"><span className="text-muted-foreground sm:hidden">Enrolled: </span>{c.enrolled}</div>
                <div className="tnum text-sm"><span className="text-muted-foreground sm:hidden">Completed: </span>{c.completions}</div>
                <div className="col-span-2 sm:col-span-1">
                  <div className="flex items-center gap-3">
                    <ProgressBar value={c.completion_rate} className="flex-1" />
                    <span className="tnum w-10 text-right text-sm font-semibold">{c.completion_rate}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Tap a course to see lesson-by-lesson drop-off.</p>
      </div>

      <DropOffModal courseId={drillCourse} onClose={() => setDrillCourse(null)} />
    </div>
  );
}
